import { showCustomConfirm } from './confirm';
import { getMaxDateISO, getMinDateISO } from './dates';
import { byId, escapeHtml } from './dom';
import { clearInlineError, showInlineError } from './errors';
import * as icons from './icons';
import { AUTOCOMPLETE_OPTIONS, extractCountry, isPlaceSpecificEnough } from './places';
import { ensureFieldState, fieldState, multiState, resetMultiTrips, saveMultiToSession } from './state';
import type { MultiTrip } from './types';
import { splitRouteAddress, whenGoogleReady } from './utils';
import { lockBodyScroll, unlockBodyScroll } from './scroll-lock';
import { validateForm } from './validation';

const MIN_TRIPS = 3;
const MAX_TRIPS = 30;
const MAX_STOPOVERS = 2;

function findTrip(id: number): MultiTrip | undefined {
  return multiState.trips.find((t) => t.id === id);
}

export function checkChronology(): boolean {
  for (let i = 0; i < multiState.trips.length - 1; i++) {
    const t1 = multiState.trips[i];
    const t2 = multiState.trips[i + 1];
    if (t1.date && t2.date) {
      const d1 = new Date(`${t1.date}T${t1.time || '00:00'}`);
      const d2 = new Date(`${t2.date}T${t2.time || '00:00'}`);
      if (d1 > d2) return false;
    }
  }
  return true;
}

// --- SUMMARY CARD W GŁÓWNYM FORMULARZU ---

/** All trips must be fully filled in and validated before the summary can show. */
export function areAllMultiTripsValid(): boolean {
  if (multiState.trips.length === 0) return false;
  return multiState.trips.every(
    (t) =>
      t.pickup.trim() &&
      t.pickupValid &&
      t.destination.trim() &&
      t.destValid &&
      t.date.trim() &&
      t.time.trim() &&
      t.stopovers.every((s, idx) => !s.trim() || t.stopoversValid[idx]),
  );
}

export function updateConfirmTripsBtnState(): void {
  const btn = byId<HTMLButtonElement>('confirmMultiTripsBtn');
  if (btn) btn.disabled = !areAllMultiTripsValid();
}

/**
 * Shows the summary card in the main form when every trip is valid,
 * otherwise shows the "+ Add multiple trips" button.
 */
export function updateSummaryView(): void {
  const summaryEl = byId('multiTripSummary');
  const addBtn = byId('openMultiModalBtn');
  if (!summaryEl || !addBtn) return;

  if (areAllMultiTripsValid()) {
    const countEl = byId('summaryCount');
    if (countEl) {
      countEl.textContent = `${multiState.trips.length} ${multiState.trips.length === 1 ? 'Trip' : 'Trips'}`;
    }

    const datesEl = byId('summaryDates');
    if (datesEl) {
      const dates = multiState.trips.map((t) => t.date).filter(Boolean).sort();
      if (dates.length > 0) {
        const formatDate = (iso: string) => {
          const [y, m, d] = iso.split('-');
          return `${d}.${m}.${y}`;
        };
        const minD = formatDate(dates[0]);
        const maxD = formatDate(dates[dates.length - 1]);
        datesEl.textContent = minD === maxD ? minD : `${minD} - ${maxD}`;
      } else {
        datesEl.textContent = '-';
      }
    }

    summaryEl.classList.remove('hidden');
    addBtn.classList.add('hidden');
  } else {
    summaryEl.classList.add('hidden');
    addBtn.classList.remove('hidden');
  }
}

// --- VIEW MODE (read-only) DLA MULTI-TRIP MODAL ---

function renderReadonlyView(): void {
  const container = byId('multiReadonlyContainer');
  if (!container) return;

  const formatDate = (iso: string) => {
    if (!iso) return '-';
    const [y, m, d] = iso.split('-');
    return `${d}/${m}/${y.slice(2)}`;
  };

  container.innerHTML = multiState.trips
    .map((trip, idx) => {
      const pickup = splitRouteAddress(trip.pickup);
      const dest = splitRouteAddress(trip.destination);

      const stopoversHTML = trip.stopovers
        .filter((s) => s.trim())
        .map((s) => {
          const stop = splitRouteAddress(s);
          return `
              <div class="readonly-stopover-row">
                <span data-tooltip="${escapeHtml(stop.main)}${escapeHtml(stop.rest)}"><strong>${escapeHtml(stop.main)}</strong><span class="route-rest">${escapeHtml(stop.rest)}</span></span>
              </div>
          `;
        })
        .join('');

      return `
        <div class="readonly-trip-card">
          <div class="readonly-mobile-header">
            <span class="mobile-trip-num">#${idx + 1}</span>
            <span class="mobile-trip-date">${formatDate(trip.date)}</span>
            <span class="mobile-trip-divider">|</span>
            <span class="mobile-trip-time">${trip.time || '-'}</span>
          </div>

          <div class="readonly-trip-row">
            <div class="readonly-route">
              <span class="trip-num-readonly">#${idx + 1}</span>
              <span class="arrow-icon arrow-down">▾</span>
              <span class="route-segment pickup-segment" data-tooltip="${escapeHtml(pickup.main)}${escapeHtml(pickup.rest)}"><strong>${escapeHtml(pickup.main)}</strong><span class="route-rest">${escapeHtml(pickup.rest)}</span></span>
              <span class="arrow-icon arrow-right">→</span>
              <span class="route-segment dest-segment" data-tooltip="${escapeHtml(dest.main)}${escapeHtml(dest.rest)}"><strong>${escapeHtml(dest.main)}</strong><span class="route-rest">${escapeHtml(dest.rest)}</span></span>
            </div>
            <div class="date-cell">${formatDate(trip.date)}</div>
            <div class="time-cell">${trip.time || '-'}</div>
            <div class="readonly-stats-wrapper">
              <div class="readonly-stat"><span class="stat-icon-readonly">${icons.svgPaxReadonly}</span> ${trip.pax}</div>
              <div class="readonly-stat"><span class="stat-icon-readonly">${icons.svgBags}</span> ${trip.bags}</div>
            </div>

            ${stopoversHTML ? `<div class="readonly-stopovers">${stopoversHTML}</div>` : ''}
            ${
              trip.notes.trim()
                ? `
              <div class="readonly-notes">
                <span class="notes-label">Extra request details</span>
                <div class="notes-content">${escapeHtml(trip.notes)}</div>
              </div>
            `
                : ''
            }
          </div>
        </div>
      `;
    })
    .join('');
}

export function showEditView(): void {
  byId('multiEditView')?.classList.remove('hidden');
  byId('multiReadonlyView')?.classList.add('hidden');
  whenGoogleReady(() => renderMultiTrips());
}

export function showReadonlyView(): void {
  byId('multiEditView')?.classList.add('hidden');
  byId('multiReadonlyView')?.classList.remove('hidden');
  renderReadonlyView();
}

// --- MULTI-TRIP MODAL LOGIC ---

export function openMultiModal(): void {
  const overlay = byId('multiModalOverlay');
  if (!overlay) return;
  const wasHidden = overlay.classList.contains('hidden');
  overlay.classList.remove('hidden');
  if (wasHidden) lockBodyScroll();
  showEditView();
}

export function openMultiModalAsView(): void {
  const overlay = byId('multiModalOverlay');
  if (!overlay) return;
  const wasHidden = overlay.classList.contains('hidden');
  overlay.classList.remove('hidden');
  if (wasHidden) lockBodyScroll();
  showReadonlyView();
}

function hideModalAndRefresh(): void {
  const overlay = byId('multiModalOverlay');
  if (overlay && !overlay.classList.contains('hidden')) {
    overlay.classList.add('hidden');
    unlockBodyScroll();
  }
  updateSummaryView();
  validateForm();
}

export function closeMultiModal(): void {
  if (multiState.trips.length > 1 && !checkChronology()) {
    showCustomConfirm(
      'Double-check your dates',
      "It looks like your trips aren't in chronological order. This may affect your route logic. Want to go back and adjust, or continue as planned?",
      'Go back',
      'Continue anyway',
      hideModalAndRefresh,
    );
  } else {
    hideModalAndRefresh();
  }
}

export function addMultiTrip(): void {
  if (multiState.trips.length >= MAX_TRIPS) {
    showCustomConfirm(
      'Trip limit reached',
      "You've hit the maximum number of trips available in a single booking request. Need more? Reach out to us directly and we'll take it from there.",
      '',
      'Got it',
      null,
    );
    return;
  }

  multiState.counter++;
  const tId = multiState.counter;
  multiState.trips.push({
    id: tId,
    pickup: '',
    pickupValid: false,
    destination: '',
    destValid: false,
    stopovers: [],
    stopoversValid: [],
    date: '',
    time: '',
    pax: 5,
    bags: 0,
    showDetails: false,
    showStopovers: false,
    notes: '',
  });

  ensureFieldState(`m_pickup_${tId}`);
  ensureFieldState(`m_dest_${tId}`);
  ensureFieldState(`m_date_${tId}`);
  ensureFieldState(`m_time_${tId}`);

  saveMultiToSession();
  renderMultiTrips();
}

/** Clears all multi-trip data and refills the list with empty trips. */
export function resetMultiTripsToEmpty(): void {
  resetMultiTrips();
  while (multiState.trips.length < MIN_TRIPS) addMultiTrip();
}

/** Fills the list up to the minimum trip count on first load. */
export function initMultiTrips(): void {
  whenGoogleReady(() => {
    if (multiState.trips.length < MIN_TRIPS) {
      while (multiState.trips.length < MIN_TRIPS) addMultiTrip();
    } else {
      renderMultiTrips();
    }
  });
}

function deleteMultiTrip(id: number): void {
  if (multiState.trips.length <= MIN_TRIPS) return;
  showCustomConfirm(
    'Remove this trip?',
    'This trip will be permanently removed from your itinerary. Depending on your route, this may affect the rest of your journey.',
    'Keep it',
    'Remove trip',
    () => {
      multiState.trips = multiState.trips.filter((t) => t.id !== id);
      saveMultiToSession();
      renderMultiTrips();
    },
  );
}

function toggleTripDetails(id: number): void {
  const trip = findTrip(id);
  if (trip) {
    trip.showDetails = !trip.showDetails;
    saveMultiToSession();
    renderMultiTrips();
  }
}

function toggleStopovers(id: number): void {
  const trip = findTrip(id);
  if (trip) {
    trip.showStopovers = !trip.showStopovers;
    saveMultiToSession();
    renderMultiTrips();
  }
}

function addStopover(id: number): void {
  const trip = findTrip(id);
  if (trip && trip.stopovers.length < MAX_STOPOVERS) {
    trip.stopovers.push('');
    trip.stopoversValid.push(false);
    ensureFieldState(`m_stop_${id}_${trip.stopovers.length - 1}`);
    trip.showStopovers = true;
    saveMultiToSession();
    renderMultiTrips();
  }
}

function removeStopover(id: number, index: number): void {
  const trip = findTrip(id);
  if (trip) {
    trip.stopovers.splice(index, 1);
    trip.stopoversValid.splice(index, 1);
    if (trip.stopovers.length === 0) trip.showStopovers = false;
    saveMultiToSession();
    renderMultiTrips();
  }
}

// --- RENDER ---

export function renderMultiTrips(): void {
  const container = byId('multiTripsContainer');
  const header = byId('multiHeaderRow');
  const confirmBtn = byId('confirmMultiTripsBtn');
  if (!container || !header || !confirmBtn) return;

  if (multiState.trips.length === 0) {
    header.classList.add('hidden');
    confirmBtn.classList.add('hidden');
    container.innerHTML = '';
    return;
  }

  header.classList.remove('hidden');
  confirmBtn.classList.remove('hidden');

  const minDateISO = getMinDateISO();
  const maxDateISO = getMaxDateISO();
  container.innerHTML = multiState.trips
    .map(
      (trip, idx) => `
        <div class="multi-trip-card" data-id="${trip.id}" draggable="true">
          <div class="trip-row-grid">
            <div class="trip-num">
              <span class="drag-handle" title="Drag to reorder">${icons.svgReorder}</span>
              <span data-action="toggle-stopovers" class="trip-number">#${idx + 1} <span class="arrow">${trip.showStopovers ? '▾' : '▸'}</span></span>
            </div>
            <div class="trip-input-group">
              <input type="text" id="m_pickup_${trip.id}" class="m-pickup" placeholder="Pickup*" value="${escapeHtml(trip.pickup)}" data-field="pickup" />
              <div class="error-message" data-for="m_pickup_${trip.id}"></div>
            </div>
            <div class="trip-add-stopover">
              <button type="button" class="icon-btn" data-action="add-stopover" ${trip.stopovers.length >= MAX_STOPOVERS ? 'disabled' : ''}>${icons.svgPlus}${trip.stopovers.length > 0 ? `<span class="stopover-badge">${trip.stopovers.length}</span>` : ''}</button>
            </div>
            <div class="trip-input-group">
              <input type="text" id="m_dest_${trip.id}" class="m-dest" placeholder="Destination*" value="${escapeHtml(trip.destination)}" data-field="destination" />
              <div class="error-message" data-for="m_dest_${trip.id}"></div>
            </div>
            <div class="trip-input-group">
              <input type="date" id="m_date_${trip.id}" value="${trip.date}" min="${minDateISO}" max="${maxDateISO}" data-field="date" />
              <div class="error-message" data-for="m_date_${trip.id}"></div>
            </div>
            <div class="trip-input-group">
              <input type="time" id="m_time_${trip.id}" value="${trip.time}" data-field="time" />
              <div class="error-message" data-for="m_time_${trip.id}"></div>
            </div>
            <div class="trip-stat-input"><input type="number" min="1" max="999" value="${trip.pax}" data-field="pax" /></div>
            <div class="trip-stat-input"><input type="number" min="0" max="999" value="${trip.bags}" data-field="bags" /></div>
            <div class="trip-actions">
              <button type="button" class="icon-btn" data-action="toggle-details">${icons.svgEdit}</button>
              <button type="button" class="icon-btn" data-action="delete-trip" ${multiState.trips.length <= MIN_TRIPS ? 'disabled' : ''}>${icons.svgDelete}</button>
            </div>
          </div>

          <div class="expanded-grid">
            <div class="stopovers-area ${trip.showStopovers ? '' : 'hidden'}">
              ${trip.stopovers
                .map(
                  (stop, sIdx) => `
                <div class="stopover-row trip-input-group">
                  <label class="stopover-label">Stopover*</label>
                  <input type="text" id="m_stop_${trip.id}_${sIdx}" class="m-stop" placeholder="Address and/or city" value="${escapeHtml(stop)}" data-field="stopover" data-idx="${sIdx}" />
                  <button type="button" class="icon-btn" data-action="remove-stopover" data-idx="${sIdx}">${icons.svgDelete}</button>
                  <div class="error-message" data-for="m_stop_${trip.id}_${sIdx}"></div>
                </div>
              `,
                )
                .join('')}
            </div>

            <div class="details-area ${trip.showDetails ? '' : 'hidden'}">
               <button type="button" class="icon-btn close-details" data-action="toggle-details">${icons.svgDelete}</button>
               <textarea placeholder="e.g. Traveling with a bike box..." data-field="notes">${escapeHtml(trip.notes)}</textarea>
            </div>
          </div>
        </div>
      `,
    )
    .join('');

  bindMultiTripEvents(container);
  bindDragAndDrop(container);
  updateConfirmTripsBtnState();
}

// --- EVENT BINDING ---

function tripIdOf(element: Element): number {
  const card = element.closest<HTMLElement>('.multi-trip-card');
  return card ? parseInt(card.dataset.id ?? '', 10) : NaN;
}

function applyFieldValue(trip: MultiTrip, field: string, value: string, stopIdx: number): void {
  switch (field) {
    case 'stopover':
      trip.stopovers[stopIdx] = value;
      trip.stopoversValid[stopIdx] = false; // reset walidacji przy wpisywaniu ręcznym
      break;
    case 'pickup':
      trip.pickup = value;
      trip.pickupValid = false;
      break;
    case 'destination':
      trip.destination = value;
      trip.destValid = false;
      break;
    case 'date':
      trip.date = value;
      break;
    case 'time':
      trip.time = value;
      break;
    case 'notes':
      trip.notes = value;
      break;
    case 'pax':
      trip.pax = Number(value) || 0;
      break;
    case 'bags':
      trip.bags = Number(value) || 0;
      break;
  }
}

function bindMultiTripEvents(container: HTMLElement): void {
  multiState.trips.forEach((t) => {
    ensureFieldState(`m_pickup_${t.id}`);
    ensureFieldState(`m_dest_${t.id}`);
    ensureFieldState(`m_date_${t.id}`);
    ensureFieldState(`m_time_${t.id}`);
    t.stopovers.forEach((_, sIdx) => ensureFieldState(`m_stop_${t.id}_${sIdx}`));
  });

  // Akcje przycisków (toggle/add/remove/delete)
  container.querySelectorAll<HTMLElement>('[data-action]').forEach((el) => {
    el.addEventListener('click', () => {
      const id = tripIdOf(el);
      if (Number.isNaN(id)) return;
      switch (el.dataset.action) {
        case 'toggle-stopovers':
          toggleStopovers(id);
          break;
        case 'toggle-details':
          toggleTripDetails(id);
          break;
        case 'add-stopover':
          addStopover(id);
          break;
        case 'remove-stopover':
          removeStopover(id, parseInt(el.dataset.idx ?? '', 10));
          break;
        case 'delete-trip':
          deleteMultiTrip(id);
          break;
      }
    });
  });

  // Nasłuchiwanie blur (aby obsłużyć touched) i input
  container.querySelectorAll<HTMLInputElement | HTMLTextAreaElement>('input, textarea').forEach((el) => {
    el.addEventListener('blur', () => {
      if (el.id && fieldState[el.id]) {
        fieldState[el.id].touched = true;
        validateForm();
      }
    });

    el.addEventListener('input', () => {
      const tripId = tripIdOf(el);
      const field = el.dataset.field;
      const trip = findTrip(tripId);
      if (!trip || !field) return;

      applyFieldValue(trip, field, el.value, parseInt(el.dataset.idx ?? '', 10));
      clearInlineError(el.id);
      saveMultiToSession();
      validateForm();
      updateConfirmTripsBtnState();
    });
  });

  bindAutocomplete(container);
}

function bindAutocomplete(container: HTMLElement): void {
  if (!window.google) return;

  container.querySelectorAll<HTMLInputElement>('.m-pickup, .m-dest, .m-stop').forEach((input) => {
    const ac = new google.maps.places.Autocomplete(input, AUTOCOMPLETE_OPTIONS);

    ac.addListener('place_changed', () => {
      const place = ac.getPlace();
      const tripId = tripIdOf(input);
      const field = input.dataset.field;
      const trip = findTrip(tripId);
      if (!trip || !field) return;

      clearInlineError(input.id);

      const markInvalid = (message: string) => {
        if (field === 'pickup') trip.pickupValid = false;
        if (field === 'destination') trip.destValid = false;
        if (field === 'stopover') trip.stopoversValid[parseInt(input.dataset.idx ?? '', 10)] = false;
        if (fieldState[input.id]) fieldState[input.id].touched = true;
        showInlineError(input.id, message);
        validateForm();
      };

      if (!place.geometry) {
        markInvalid('Please select the full street address from the dropdown.');
        return;
      }
      if (!isPlaceSpecificEnough(place)) {
        markInvalid('Please select the full street address from the dropdown.');
        return;
      }

      const displayValue =
        place.name && place.types?.includes('establishment')
          ? `${place.name}, ${place.formatted_address}`
          : place.formatted_address ?? '';

      if (field === 'stopover') {
        const idx = parseInt(input.dataset.idx ?? '', 10);
        trip.stopovers[idx] = displayValue;
        trip.stopoversValid[idx] = true;
      } else if (field === 'pickup') {
        trip.pickup = displayValue;
        trip.pickupValid = true;
        trip.pickupCountry = extractCountry(place.address_components);
      } else if (field === 'destination') {
        trip.destination = displayValue;
        trip.destValid = true;
      }

      input.value = displayValue;
      saveMultiToSession();
      validateForm();
      updateConfirmTripsBtnState();
    });
  });
}

function bindDragAndDrop(container: HTMLElement): void {
  const cards = container.querySelectorAll<HTMLElement>('.multi-trip-card');
  let draggedItem: HTMLElement | null = null;
  let draggedIndex = -1;

  cards.forEach((card, index) => {
    card.addEventListener('dragstart', (e) => {
      draggedItem = card;
      draggedIndex = index;
      if (e.dataTransfer) e.dataTransfer.effectAllowed = 'move';
      // setTimeout zapobiega zniknięciu elementu, który aktualnie ciągniemy
      setTimeout(() => card.classList.add('dragging'), 0);
    });

    card.addEventListener('dragend', () => {
      card.classList.remove('dragging');
      draggedItem = null;
    });

    card.addEventListener('dragover', (e) => {
      e.preventDefault(); // wymagane, aby drop zadziałał
      if (e.dataTransfer) e.dataTransfer.dropEffect = 'move';
    });

    card.addEventListener('dragenter', (e) => {
      e.preventDefault();
      if (card !== draggedItem) card.classList.add('drag-over');
    });

    card.addEventListener('dragleave', () => {
      card.classList.remove('drag-over');
    });

    card.addEventListener('drop', (e) => {
      e.preventDefault();
      card.classList.remove('drag-over');

      if (card !== draggedItem && draggedIndex !== -1) {
        const [movedTrip] = multiState.trips.splice(draggedIndex, 1);
        multiState.trips.splice(index, 0, movedTrip);
        saveMultiToSession();
        renderMultiTrips();
      }
    });
  });
}
